import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  collection, query, where, writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { getDocument, buildMatchData } from '../utils/matchHelpers';
import AppDialog from '../components/AppDialog';
import KnockoutBracket from '../components/KnockoutBracket';

// ─── Constants ────────────────────────────────────────────────────────────────

const UPLOAD_URL = process.env.REACT_APP_UPLOAD_URL || '';
const DELETE_URL = UPLOAD_URL.replace('upload.php', 'delete.php');

const MAX_SIZE_BYTES = 2 * 1024 * 1024;
const MAX_DIMENSION  = 2000;
const ALLOWED_TYPES  = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// ─── Utilities ────────────────────────────────────────────────────────────────

function validateImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION)
        reject(new Error(`Image is ${img.width}×${img.height} px — max ${MAX_DIMENSION}×${MAX_DIMENSION}`));
      else resolve();
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });
}

async function getAuthToken() {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Not authenticated');
  return token;
}


async function uploadPoster(tournamentId, file) {
  const token = await getAuthToken();
  const form  = new FormData();
  form.append('poster', file);
  form.append('tournament_id', tournamentId);
  const res  = await fetch(UPLOAD_URL, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Upload failed');
  return json.poster_url;
}

async function deletePoster(posterUrl) {
  if (!posterUrl) return;
  try {
    const filename = new URL(posterUrl).pathname.split('/').pop();
    const token    = await getAuthToken();
    await fetch(DELETE_URL, { method: 'DELETE', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ filename }) });
  } catch { /* best-effort */ }
}

// Compute group point standings from matches
function computeGroupStandings(groupId, teams, matches) {
  const groupTeams   = teams.filter(t => t.group_id === groupId);
  const groupMatches = matches.filter(m => m.group_id === groupId && m.match_type === 'group' && m.status === 'completed');

  const standings = groupTeams.map(team => {
    let wins = 0, losses = 0, ptsFor = 0, ptsAgainst = 0, played = 0;
    groupMatches.forEach(m => {
      const isT1 = m.team1_id === team.id;
      const isT2 = m.team2_id === team.id;
      if (!isT1 && !isT2) return;
      played++;
      // Sum across all sets for accurate point totals
      let scored = 0, against = 0;
      try {
        const sets = JSON.parse(m.set_scores || '[]');
        if (sets.length > 0) {
          sets.forEach(s => { scored += isT1 ? (parseInt(s.t1, 10) || 0) : (parseInt(s.t2, 10) || 0); against += isT1 ? (parseInt(s.t2, 10) || 0) : (parseInt(s.t1, 10) || 0); });
        } else {
          scored  = isT1 ? (m.team1_score || 0) : (m.team2_score || 0);
          against = isT1 ? (m.team2_score || 0) : (m.team1_score || 0);
        }
      } catch {
        scored  = isT1 ? (m.team1_score || 0) : (m.team2_score || 0);
        against = isT1 ? (m.team2_score || 0) : (m.team1_score || 0);
      }
      ptsFor     += scored;
      ptsAgainst += against;
      if (m.winner_team_id === team.id) wins++;
      else losses++;
    });
    return { ...team, played, wins, losses, ptsFor, ptsAgainst, tournamentPoints: wins * 2 };
  });

  return standings.sort((a, b) =>
    b.tournamentPoints - a.tournamentPoints ||
    (b.ptsFor - b.ptsAgainst) - (a.ptsFor - a.ptsAgainst) ||
    b.ptsFor - a.ptsFor
  );
}

// Build bracket seeding: groups × qualifyPerGroup → seeded list
function buildKnockoutSeeds(groups, qualifyPerGroup, standingsMap) {
  const seeds = [];
  // Collect by rank: all rank-1 from each group, then all rank-2, etc.
  for (let rank = 0; rank < qualifyPerGroup; rank++) {
    for (const g of groups) {
      const standings = standingsMap[g.id] || [];
      if (standings[rank]) seeds.push({ ...standings[rank], seed: seeds.length + 1, fromGroup: g.name });
    }
  }
  return seeds;
}

// Bracket size = next power of 2 >= n
function nextPow2(n) {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

// Standard bracket seeding order for n positions (n must be power of 2)
function buildSeedOrder(n) {
  if (n === 1) return [1];
  if (n === 2) return [1, 2];
  const half  = buildSeedOrder(n / 2);
  const other = half.map(s => n + 1 - s);
  const result = [];
  for (let i = 0; i < half.length; i++) {
    result.push(half[i], other[i]);
  }
  return result;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  // ── Core data
  const [data,    setData]    = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── UI state
  const [activeTab,       setActiveTab]       = useState('teams');
  const [saving,          setSaving]          = useState(false);
  const [dialog,          setDialog]          = useState(null);
  const [posterUploading, setPosterUploading] = useState(false);
  const posterInputRef = useRef(null);

  // ── Team modal
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamForm,      setTeamForm]      = useState({ player1_id: '', player2_id: '', name: '' });

  // ── Match generation modal
  const [showMatchModal,   setShowMatchModal]   = useState(false);
  const [matchGenMode,     setMatchGenMode]     = useState('random');
  const [showManualMatch,  setShowManualMatch]  = useState(false);
  const [manualMatches,    setManualMatches]    = useState([{ team1_id: '', team2_id: '', round: 1 }]);

  // ── Group setup state
  const [showGroupSetup,   setShowGroupSetup]   = useState(false);
  const [teamsPerGroup,    setTeamsPerGroup]    = useState(4);
  const [assignMode,       setAssignMode]       = useState('random');
  const [manualGroupMap,   setManualGroupMap]   = useState({}); // teamId → groupIndex

  // ── Group match schedule inline edits
  const [scheduleEdits, setScheduleEdits] = useState({}); // matchId → {time:'', court:''}
  const [savingSchedule, setSavingSchedule] = useState({});

  // ── Knockout setup state
  const [showKnockoutSetup,  setShowKnockoutSetup]  = useState(false);
  const [qualifyPerGroup,    setQualifyPerGroup]    = useState(2);
  const [wildcardChoice,     setWildcardChoice]     = useState(null); // teamId for wildcard bye decision
  const [wildcardCandidates, setWildcardCandidates] = useState([]);

  // ── Add-result modal (reuse from MatchCard)
  const [addResultMatch, setAddResultMatch] = useState(null);

  const showAlert   = (t, m) => setDialog({ title: t, message: m });
  const showConfirm = (t, m, onConfirm) =>
    setDialog({ title: t, message: m, onConfirm, danger: true, confirmText: 'Confirm', cancelText: 'Cancel' });

  // ─── Load all data ───────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tournSnap, teamsSnap, matchesSnap, playersSnap, groupsSnap] = await Promise.all([
        getDoc(doc(db, 'tournaments', id)),
        getDocs(query(collection(db, 'teams'),   where('tournament_id', '==', id))),
        getDocs(query(collection(db, 'matches'), where('tournament_id', '==', id))),
        getDocs(collection(db, 'players')),
        getDocs(query(collection(db, 'groups'),  where('tournament_id', '==', id))),
      ]);

      if (!tournSnap.exists()) { setLoading(false); return; }
      const tournament = { id: tournSnap.id, ...tournSnap.data() };

      const playerMap = {};
      playersSnap.docs.forEach(d => { playerMap[d.id] = { id: d.id, ...d.data() }; });
      setPlayers(Object.values(playerMap).sort((a, b) => a.name.localeCompare(b.name)));

      const teamDocs = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const teamMap  = {};
      teamDocs.forEach(t => { teamMap[t.id] = t; });

      const teams = teamDocs.map(t => ({
        ...t,
        player1_name: playerMap[t.player1_id]?.name || null,
        player2_name: playerMap[t.player2_id]?.name || null,
      })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      const matches = matchesSnap.docs.map(d => {
        const m  = { id: d.id, ...d.data() };
        const t1 = teamMap[m.team1_id];
        const t2 = teamMap[m.team2_id];
        return {
          ...m,
          team1_name:        t1?.name || null,
          team2_name:        t2?.name || null,
          team1_player1_name: playerMap[t1?.player1_id]?.name || null,
          team1_player2_name: playerMap[t1?.player2_id]?.name || null,
          team2_player1_name: playerMap[t2?.player1_id]?.name || null,
          team2_player2_name: playerMap[t2?.player2_id]?.name || null,
        };
      }).sort((a, b) => (a.round || 0) - (b.round || 0) || (a.match_order || 0) - (b.match_order || 0));

      const groups = groupsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      setData({ ...tournament, teams, matches, groups, teamMap });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ─── Default tab based on format ────────────────────────────────────────────

  useEffect(() => {
    if (!data) return;
    if (data.format === 'group_knockout') {
      const hasGroups = data.groups?.length > 0;
      setActiveTab(hasGroups ? 'group_matches' : 'teams');
    } else {
      setActiveTab('bracket');
    }
  }, [data?.format, data?.groups?.length]);

  // ─── Loading screen ──────────────────────────────────────────────────────────

  if (loading && !data) {
    return (
      <div className="container" style={{ padding: '48px 20px', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 16px' }} />
        <div style={{ color: '#7f8c8d', fontSize: '15px' }}>Loading tournament…</div>
      </div>
    );
  }

  if (!data) return <div className="container" style={{ padding: '32px' }}>Tournament not found.</div>;

  const { teams, matches, groups } = data;
  const isGroupKnockout = data.format === 'group_knockout';
  const tournamentApproved = (data.approval_status || 'pending') === 'approved';

  // ─── Derived data ────────────────────────────────────────────────────────────

  const groupMatches   = matches.filter(m => m.match_type === 'group');
  const knockoutMatches = matches.filter(m => m.match_type === 'knockout');

  // Group standings per group
  const standingsMap = {};
  groups.forEach(g => { standingsMap[g.id] = computeGroupStandings(g.id, teams, matches); });

  // teamMap for bracket
  const teamMapForBracket = {};
  teams.forEach(t => { teamMapForBracket[t.id] = t; });

  const rounds = [...new Set(matches.filter(m => !m.match_type || m.match_type !== 'knockout').map(m => m.round))].sort((a, b) => a - b);
  const statusMap = { scheduled: '⏳', setup: '⚙️', active: '🔴', completed: '✅' };
  const availablePlayers = players.filter(p => !teams.some(t => t.player1_id === p.id || t.player2_id === p.id));

  // ─── Handlers: Teams ─────────────────────────────────────────────────────────

  const handleCreatePlayer = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Player name is required.');
    const duplicate = players.some(p => p.name.toLowerCase() === trimmed.toLowerCase());
    if (duplicate) throw new Error(`"${trimmed}" already exists.`);
    const ref = await addDoc(collection(db, 'players'), {
      name: trimmed, email: null, phone: null,
      wins: 0, losses: 0, points_scored: 0, points_against: 0, matches_played: 0,
      created_at: new Date().toISOString(),
    });
    const snap = await getDocs(collection(db, 'players'));
    setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name)));
    return ref.id;
  };

  const handleAddTeam = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const p1 = await getDocument('players', teamForm.player1_id);
      let teamName = teamForm.name;
      if (!teamName) {
        if (data.type === 'doubles' && teamForm.player2_id) {
          const p2 = await getDocument('players', teamForm.player2_id);
          teamName = `${p1.name} & ${p2.name}`;
        } else {
          teamName = p1.name;
        }
      }
      await addDoc(collection(db, 'teams'), {
        tournament_id: id,
        player1_id: teamForm.player1_id,
        player2_id: teamForm.player2_id || null,
        name: teamName,
        group_id: null,
        wins: 0, losses: 0, points_for: 0, points_against: 0,
      });
      await load();
      setShowTeamModal(false);
      setTeamForm({ player1_id: '', player2_id: '', name: '' });
    } catch (err) { showAlert('Error', err.message || 'Error adding team'); }
    finally { setSaving(false); }
  };

  const handleRemoveTeam = (teamId) => {
    showConfirm('Remove Team', 'Remove this team from the tournament?', async () => {
      await deleteDoc(doc(db, 'teams', teamId));
      load();
    });
  };

  // ─── Handlers: Matches (non-group) ───────────────────────────────────────────

  const handleGenerateMatches = async () => {
    setSaving(true);
    try {
      const existingSnap = await getDocs(query(collection(db, 'matches'), where('tournament_id', '==', id)));
      const deleteBatch  = writeBatch(db);
      existingSnap.docs.filter(d => d.data().status === 'scheduled').forEach(d => deleteBatch.delete(d.ref));
      await deleteBatch.commit();

      const matchDataList = [];
      let order = 1;
      if (matchGenMode === 'manual' && manualMatches) {
        manualMatches.filter(m => m.team1_id && m.team2_id).forEach((m, i) => {
          matchDataList.push(buildMatchData(id, m.team1_id, m.team2_id, m.round || 1, i + 1, data.points_to_win, data.type || 'singles', data.sets_to_win || 1));
        });
      } else if (data.format === 'single_elimination') {
        const shuffled = matchGenMode === 'random' ? [...teams].sort(() => Math.random() - 0.5) : teams;
        for (let i = 0; i < shuffled.length - 1; i += 2) {
          if (shuffled[i + 1]) matchDataList.push(buildMatchData(id, shuffled[i].id, shuffled[i + 1].id, 1, order++, data.points_to_win, data.type || 'singles', data.sets_to_win || 1));
        }
      } else {
        for (let i = 0; i < teams.length; i++)
          for (let j = i + 1; j < teams.length; j++)
            matchDataList.push(buildMatchData(id, teams[i].id, teams[j].id, 1, order++, data.points_to_win, data.type || 'singles', data.sets_to_win || 1));
      }

      const insertBatch = writeBatch(db);
      matchDataList.forEach(m => insertBatch.set(doc(collection(db, 'matches')), m));
      await insertBatch.commit();

      await load();
      setShowMatchModal(false);
      setShowManualMatch(false);
    } catch (err) { showAlert('Error', err.message || 'Error generating matches'); }
    finally { setSaving(false); }
  };

  // ─── Handlers: Tournament status ─────────────────────────────────────────────

  const handleUpdateStatus = async (status) => {
    if (status === 'active' && (data.approval_status || 'pending') !== 'approved') {
      showAlert('Approval Required', 'This tournament must be approved before it can be started.');
      return;
    }
    await updateDoc(doc(db, 'tournaments', id), { status });
    load();
  };

  // ─── Handlers: Poster ────────────────────────────────────────────────────────

  const handlePosterChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) { showAlert('Invalid Type', 'Only JPEG, PNG, WebP, GIF.'); e.target.value = ''; return; }
    if (file.size > MAX_SIZE_BYTES)          { showAlert('Too Large',    'Max 2 MB.');                  e.target.value = ''; return; }
    try { await validateImageDimensions(file); } catch (err) { showAlert('Image Too Large', err.message); e.target.value = ''; return; }
    setPosterUploading(true);
    try {
      const url = await uploadPoster(id, file);
      await updateDoc(doc(db, 'tournaments', id), { poster_url: url });
      await load();
    } catch (err) { showAlert('Upload Failed', err.message);
    } finally { setPosterUploading(false); if (posterInputRef.current) posterInputRef.current.value = ''; }
  };

  const handleRemovePoster = () => setDialog({
    title: 'Remove Poster', message: 'Remove the tournament poster?',
    onConfirm: async () => {
      setPosterUploading(true);
      try { await deletePoster(data.poster_url); await updateDoc(doc(db, 'tournaments', id), { poster_url: null }); await load(); }
      catch (err) { showAlert('Error', err.message); }
      finally { setPosterUploading(false); }
    },
    danger: true, confirmText: 'Remove', cancelText: 'Cancel',
  });

  // ─── Handlers: Group Setup ───────────────────────────────────────────────────

  const handleSetupGroups = async () => {
    if (teams.length < 2) { showAlert('Not Enough Teams', 'Add at least 2 teams first.'); return; }
    setSaving(true);
    try {
      const numGroups  = Math.ceil(teams.length / teamsPerGroup);
      const batch      = writeBatch(db);

      // Delete existing groups
      if (groups.length > 0) {
        groups.forEach(g => batch.delete(doc(db, 'groups', g.id)));
      }

      // Assign teams
      let teamList = [...teams];
      if (assignMode === 'random') {
        for (let i = teamList.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [teamList[i], teamList[j]] = [teamList[j], teamList[i]];
        }
      }

      // Create group docs and assign teams
      const groupRefs = [];
      for (let i = 0; i < numGroups; i++) {
        const gRef = doc(collection(db, 'groups'));
        groupRefs.push(gRef.id);
        batch.set(gRef, {
          tournament_id: id,
          name: `Group ${String.fromCharCode(65 + i)}`,
          order: i,
          created_at: new Date().toISOString(),
        });
      }

      if (assignMode === 'manual') {
        teams.forEach(t => {
          const gIdx  = manualGroupMap[t.id] ?? 0;
          const gId   = groupRefs[Math.min(gIdx, groupRefs.length - 1)];
          batch.update(doc(db, 'teams', t.id), { group_id: gId });
        });
      } else {
        teamList.forEach((t, i) => {
          const gIdx = i % numGroups;
          batch.update(doc(db, 'teams', t.id), { group_id: groupRefs[gIdx] });
        });
      }

      await batch.commit();
      await load();
      setShowGroupSetup(false);
      setActiveTab('group_matches');
    } catch (err) { showAlert('Error', err.message); }
    finally { setSaving(false); }
  };

  // ─── Handlers: Group Matches ──────────────────────────────────────────────

  const handleGenerateGroupMatches = async (matchesPerPair = 1) => {
    setSaving(true);
    try {
      // Delete existing scheduled group matches
      const toDelete = matches.filter(m => m.match_type === 'group' && m.status === 'scheduled');
      if (toDelete.length > 0) {
        const delBatch = writeBatch(db);
        toDelete.forEach(m => delBatch.delete(doc(db, 'matches', m.id)));
        await delBatch.commit();
      }

      const insertBatch = writeBatch(db);
      let order = 1;
      groups.forEach(g => {
        const gTeams = teams.filter(t => t.group_id === g.id);
        for (let i = 0; i < gTeams.length; i++) {
          for (let j = i + 1; j < gTeams.length; j++) {
            for (let k = 0; k < matchesPerPair; k++) {
              const mRef = doc(collection(db, 'matches'));
              insertBatch.set(mRef, {
                ...buildMatchData(id, gTeams[i].id, gTeams[j].id, 1, order++, data.points_to_win, data.type || 'singles', data.sets_to_win || 1),
                match_type: 'group',
                group_id: g.id,
                group_name: g.name,
                scheduled_time: null,
              });
            }
          }
        }
      });
      await insertBatch.commit();
      await load();
    } catch (err) { showAlert('Error', err.message); }
    finally { setSaving(false); }
  };

  // Inline schedule save
  const handleSaveSchedule = async (matchId) => {
    const edit = scheduleEdits[matchId];
    if (!edit) return;
    setSavingSchedule(s => ({ ...s, [matchId]: true }));
    try {
      await updateDoc(doc(db, 'matches', matchId), {
        scheduled_time: edit.time || null,
        court_number: parseInt(edit.court, 10) || 1,
      });
      setScheduleEdits(s => { const n = { ...s }; delete n[matchId]; return n; });
      await load();
    } catch (err) { showAlert('Error', err.message); }
    finally { setSavingSchedule(s => ({ ...s, [matchId]: false })); }
  };

  // ─── Handlers: Knockout Generation ──────────────────────────────────────────

  const handleGenerateKnockout = async () => {
    setSaving(true);
    try {
      const seeds = buildKnockoutSeeds(groups, qualifyPerGroup, standingsMap);

      if (seeds.length < 2) { showAlert('Not Enough Qualifiers', 'Need at least 2 qualifying teams.'); setSaving(false); return; }

      const bracketSize = nextPow2(seeds.length);
      const byeCount    = bracketSize - seeds.length;

      // Delete existing knockout matches
      const toDelete = knockoutMatches.filter(m => m.status === 'scheduled');
      if (toDelete.length > 0) {
        const delBatch = writeBatch(db);
        toDelete.forEach(m => delBatch.delete(doc(db, 'matches', m.id)));
        await delBatch.commit();
      }

      // Pre-create all match document refs so we can link next_match_id
      const numRounds = Math.log2(bracketSize);
      const matchRefs = []; // [round_index][match_index] = docRef

      // Count matches per round
      const roundSizes = [];
      let sz = bracketSize / 2;
      while (sz >= 1) { roundSizes.push(sz); sz = Math.floor(sz / 2); }

      roundSizes.forEach(rSz => {
        const refs = [];
        for (let i = 0; i < rSz; i++) refs.push(doc(collection(db, 'matches')));
        matchRefs.push(refs);
      });

      // Bracket seeding positions (standard)
      const seedPositions = buildSeedOrder(bracketSize); // array of length bracketSize, value = seed (1-based)
      // positions[i] = seedPositions[i] → index into seeds array (0-based)

      const batch = writeBatch(db);

      // Round 0 (first round)
      const firstRoundCount = roundSizes[0];
      for (let pos = 0; pos < firstRoundCount; pos++) {
        const t1SeedIdx = seedPositions[pos * 2]     - 1; // 0-based
        const t2SeedIdx = seedPositions[pos * 2 + 1] - 1;
        const t1Seed    = seeds[t1SeedIdx];
        const t2Seed    = seeds[t2SeedIdx];
        const isBye     = !t1Seed || !t2Seed;

        const nextRoundPos = Math.floor(pos / 2);
        const nextMatchRef = matchRefs[1]?.[nextRoundPos];
        const nextSlot     = pos % 2 === 0 ? 'team1' : 'team2';

        const matchData = {
          ...buildMatchData(id, t1Seed?.id || null, t2Seed?.id || null, 1, pos + 1, data.points_to_win, data.type || 'singles', data.sets_to_win || 1),
          match_type: 'knockout',
          knockout_round_num: bracketSize,
          knockout_position: pos + 1,
          next_match_id: nextMatchRef?.id || null,
          next_match_slot: nextSlot,
          is_bye: isBye,
          scheduled_time: null,
          group_id: null,
          group_name: null,
        };

        // Auto-complete bye matches immediately
        if (isBye) {
          const winner = t1Seed?.id || t2Seed?.id || null;
          matchData.status       = 'completed';
          matchData.winner_team_id = winner;
          matchData.completed_at = new Date().toISOString();
          // Advance winner to next match
          if (nextMatchRef && winner) {
            // We'll handle this by setting the slot in next match creation
            // Store for later use
          }
        }

        batch.set(matchRefs[0][pos], matchData);
      }

      // Later rounds (empty, waiting for winners)
      for (let rIdx = 1; rIdx < matchRefs.length; rIdx++) {
        const roundNum = bracketSize / Math.pow(2, rIdx);
        matchRefs[rIdx].forEach((ref, pos) => {
          const nextRoundPos = Math.floor(pos / 2);
          const nextMatchRef = matchRefs[rIdx + 1]?.[nextRoundPos];
          const nextSlot     = pos % 2 === 0 ? 'team1' : 'team2';

          batch.set(ref, {
            ...buildMatchData(id, null, null, rIdx + 1, pos + 1, data.points_to_win, data.type || 'singles', data.sets_to_win || 1),
            match_type: 'knockout',
            knockout_round_num: roundNum,
            knockout_position: pos + 1,
            next_match_id: nextMatchRef?.id || null,
            next_match_slot: nextSlot,
            is_bye: false,
            scheduled_time: null,
            group_id: null,
            group_name: null,
          });
        });
      }

      await batch.commit();

      // Now resolve bye winners into next matches
      // Re-read and advance any byes
      const newMatchesSnap = await getDocs(query(collection(db, 'matches'), where('tournament_id', '==', id), where('match_type', '==', 'knockout')));
      const byeBatch = writeBatch(db);
      let hasBye = false;
      newMatchesSnap.docs.forEach(d => {
        const m = d.data();
        if (m.is_bye && m.winner_team_id && m.next_match_id && m.next_match_slot) {
          const field = m.next_match_slot === 'team1' ? 'team1_id' : 'team2_id';
          byeBatch.update(doc(db, 'matches', m.next_match_id), { [field]: m.winner_team_id });
          hasBye = true;
        }
      });
      if (hasBye) await byeBatch.commit();

      await updateDoc(doc(db, 'tournaments', id), { knockout_generated: true });
      await load();
      setShowKnockoutSetup(false);
      setActiveTab('bracket');
    } catch (err) { showAlert('Error', err.message); }
    finally { setSaving(false); }
  };

  // ─── Tab definitions ──────────────────────────────────────────────────────────

  const tabs = isGroupKnockout
    ? [
        { key: 'teams',           label: '👥 Teams' },
        { key: 'groups',          label: '🔠 Groups' },
        { key: 'group_matches',   label: '🗓 Group Matches' },
        { key: 'group_standings', label: '📊 Standings' },
        { key: 'bracket',         label: '🏆 Knockout' },
      ]
    : [
        { key: 'bracket',   label: '🗓 Matches' },
        { key: 'teams',     label: '👥 Teams' },
        { key: 'standings', label: '📊 Standings' },
      ];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="container" style={{ padding: '32px 20px' }}>

      {/* Poster banner */}
      {data.poster_url && (
        <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', marginBottom: '20px', maxHeight: '220px', background: '#f0f4f8' }}>
          <img src={data.poster_url} alt={data.name} style={{ width: '100%', maxHeight: '220px', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '6px' }}>
            <label style={{ padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none' }}>
              {posterUploading ? 'Uploading…' : '✏️ Change'}
              <input ref={posterInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} onChange={handlePosterChange} disabled={posterUploading} />
            </label>
            <button onClick={handleRemovePoster} disabled={posterUploading} style={{ padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: 'rgba(231,76,60,0.8)', color: '#fff', border: 'none' }}>
              🗑 Remove
            </button>
          </div>
        </div>
      )}

      {/* Champion banner — shown when tournament is completed */}
      {data.status === 'completed' && data.champion_team_id && (() => {
        const champion = teams.find(t => t.id === data.champion_team_id);
        const runnerUp = teams.find(t => t.id === data.runner_up_team_id);
        return (
          <div style={{ borderRadius: '12px', overflow: 'hidden', marginBottom: '20px', background: 'linear-gradient(135deg,#1a1a2e 0%,#16213e 60%,#0f3460 100%)', padding: '28px 24px', textAlign: 'center', position: 'relative' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(243,156,18,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ fontSize: '48px', marginBottom: '8px' }}>🏆</div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#f39c12', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '6px' }}>Tournament Champion</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: '#fff', marginBottom: '16px' }}>{champion?.name || '—'}</div>
            {runnerUp && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.08)', borderRadius: '8px', padding: '8px 16px' }}>
                <span style={{ fontSize: '18px' }}>🥈</span>
                <div>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '1px' }}>Runner-up</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'rgba(255,255,255,0.85)' }}>{runnerUp.name}</div>
                </div>
              </div>
            )}
            <div style={{ marginTop: '20px' }}>
              <Link to={`/tournament-results/${id}`}
                style={{ display: 'inline-block', padding: '10px 24px', borderRadius: '10px', background: 'rgba(243,156,18,0.15)', border: '1px solid rgba(243,156,18,0.4)', color: '#f39c12', fontSize: '13px', fontWeight: '700', textDecoration: 'none', letterSpacing: '0.3px' }}>
                🏅 View Full Results Page
              </Link>
            </div>
          </div>
        );
      })()}

      {/* Approval warning */}
      {!tournamentApproved && (
        <div style={{ background: '#fff8e1', border: '1px solid #f39c12', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px', color: '#856404', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>⏳</span>
          <div>
            <strong>Pending Super Admin Approval</strong>
            <div style={{ fontSize: '13px', marginTop: '2px' }}>
              {data.approval_status === 'rejected' ? 'Rejected by super admin.' : 'Awaiting approval — teams and schedule can be set up now.'}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/tournaments')}>← Back</button>
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: '800' }}>{data.name}</h1>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            <span className={`badge badge-${data.status}`}>{data.status}</span>
            <span className={`badge badge-${data.type}`}>{data.type}</span>
            <span className="tag">{data.format?.replace(/_/g, ' ')}</span>
            <span className="tag">To {data.points_to_win} pts</span>
            {!tournamentApproved && <span style={{ background: '#fff8e1', color: '#f39c12', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>Pending Approval</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {!data.poster_url && (
            <label style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', background: '#f0f4f8', color: '#7f8c8d', border: '1px solid #e0e0e0' }}>
              {posterUploading ? 'Uploading…' : '🖼 Add Poster'}
              <input ref={posterInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} onChange={handlePosterChange} disabled={posterUploading} />
            </label>
          )}
          <Link to={`/tournament-results/${id}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', textDecoration: 'none', background: data.status === 'completed' ? 'linear-gradient(135deg,#f39c12,#e67e22)' : '#f0f4f8', color: data.status === 'completed' ? '#fff' : '#555', border: data.status === 'completed' ? 'none' : '1px solid #e0e0e0', boxShadow: data.status === 'completed' ? '0 2px 12px rgba(243,156,18,0.35)' : 'none' }}>
            📊 {data.status === 'completed' ? 'Tournament Results' : 'Statistics'}
          </Link>
          {data.status === 'setup' && (
            <button className="btn btn-primary" onClick={() => handleUpdateStatus('active')} disabled={!tournamentApproved} title={!tournamentApproved ? 'Awaiting approval' : ''}>
              ▶ Start Tournament
            </button>
          )}
          {data.status === 'active' && (
            <button className="btn btn-secondary" onClick={() => handleUpdateStatus('completed')}>✓ Complete</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: '#f0f4f8', borderRadius: '8px', padding: '4px', marginBottom: '24px', overflowX: 'auto' }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', background: activeTab === tab.key ? '#fff' : 'transparent', color: activeTab === tab.key ? '#27ae60' : '#7f8c8d', boxShadow: activeTab === tab.key ? '0 2px 8px rgba(0,0,0,0.08)' : 'none', border: 'none', cursor: 'pointer' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Teams ── */}
      {activeTab === 'teams' && (
        <div className="animate-fadein">
          <div className="page-header">
            <h2 style={{ fontSize: '18px' }}>Teams ({teams.length})</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setShowTeamModal(true)}>+ Add Team</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
            {teams.map(t => {
              const groupName = groups.find(g => g.id === t.group_id)?.name;
              return (
                <div key={t.id} className="card" style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '4px' }}>{t.name}</div>
                      <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                        👤 {t.player1_name}{t.player2_name && <><br />👤 {t.player2_name}</>}
                      </div>
                      {groupName && <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: '600', color: '#27ae60' }}>{groupName}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '12px', color: '#7f8c8d' }}>W/L</div>
                      <div style={{ fontWeight: '700' }}>{t.wins}/{t.losses}</div>
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ color: '#e74c3c', marginTop: '10px', width: '100%' }} onClick={() => handleRemoveTeam(t.id)}>Remove</button>
                </div>
              );
            })}
            {teams.length === 0 && (
              <div className="card" style={{ gridColumn: '1/-1' }}>
                <div className="empty-state" style={{ padding: '32px' }}>
                  <div className="icon">👥</div><h3>No teams yet</h3><p>Add teams to build the bracket</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Groups (group_knockout only) ── */}
      {activeTab === 'groups' && isGroupKnockout && (
        <div className="animate-fadein">
          <div className="page-header">
            <h2 style={{ fontSize: '18px' }}>Groups {groups.length > 0 && `(${groups.length})`}</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowGroupSetup(true)} disabled={teams.length < 2}>
                {groups.length > 0 ? '🔄 Reassign Groups' : '⚡ Setup Groups'}
              </button>
              {groups.length > 0 && (
                <button className="btn btn-primary btn-sm" onClick={() => { setActiveTab('group_matches'); handleGenerateGroupMatches(1); }} disabled={saving}>
                  {saving ? 'Generating…' : '🗓 Generate Matches'}
                </button>
              )}
            </div>
          </div>
          {groups.length === 0 ? (
            <div className="card"><div className="empty-state" style={{ padding: '40px' }}>
              <div className="icon">🔠</div>
              <h3>No groups yet</h3>
              <p>Setup groups to assign teams and generate matches</p>
              <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => setShowGroupSetup(true)} disabled={teams.length < 2}>
                Setup Groups
              </button>
            </div></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {groups.map(g => {
                const gTeams = teams.filter(t => t.group_id === g.id);
                return (
                  <div key={g.id} className="card" style={{ padding: '16px' }}>
                    <div style={{ fontWeight: '800', fontSize: '16px', color: '#27ae60', marginBottom: '12px' }}>{g.name}</div>
                    {gTeams.length === 0
                      ? <div style={{ color: '#bdc3c7', fontSize: '13px' }}>No teams assigned</div>
                      : gTeams.map((t, i) => (
                        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: i < gTeams.length - 1 ? '1px solid #f0f4f8' : 'none' }}>
                          <span style={{ fontSize: '12px', color: '#7f8c8d', minWidth: '16px' }}>{i + 1}.</span>
                          <span style={{ fontWeight: '600', fontSize: '14px' }}>{t.name}</span>
                        </div>
                      ))
                    }
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Group Matches ── */}
      {activeTab === 'group_matches' && isGroupKnockout && (
        <div className="animate-fadein">
          <div className="page-header">
            <h2 style={{ fontSize: '18px' }}>Group Matches ({groupMatches.length})</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              {groups.length > 0 && (
                <button className="btn btn-secondary btn-sm" onClick={() => handleGenerateGroupMatches(1)} disabled={saving}>
                  {saving ? 'Generating…' : '⚡ Regenerate Matches'}
                </button>
              )}
            </div>
          </div>
          {groupMatches.length === 0 ? (
            <div className="card"><div className="empty-state" style={{ padding: '40px' }}>
              <div className="icon">🗓</div><h3>No group matches</h3>
              <p>Setup groups first, then generate matches</p>
            </div></div>
          ) : (
            groups.map(g => {
              const gMatches = groupMatches.filter(m => m.group_id === g.id);
              if (gMatches.length === 0) return null;
              return (
                <div key={g.id} style={{ marginBottom: '28px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#27ae60', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                    {g.name}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
                    {gMatches.map(m => (
                      <GroupMatchCard
                        key={m.id}
                        match={m}
                        statusMap={statusMap}
                        scheduleEdit={scheduleEdits[m.id]}
                        savingSchedule={savingSchedule[m.id]}
                        onScheduleChange={(field, val) => setScheduleEdits(s => ({ ...s, [m.id]: { ...s[m.id], [field]: val } }))}
                        onScheduleSave={() => handleSaveSchedule(m.id)}
                        onNavigate={navigate}
                        onAddResult={() => setAddResultMatch(m)}
                        tournamentApproved={tournamentApproved}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Tab: Group Standings ── */}
      {activeTab === 'group_standings' && isGroupKnockout && (
        <div className="animate-fadein">
          <h2 style={{ fontSize: '18px', marginBottom: '20px' }}>Group Standings</h2>
          {groups.length === 0
            ? <div className="card"><div className="empty-state"><div className="icon">📊</div><h3>Setup groups first</h3></div></div>
            : groups.map(g => {
              const rows = standingsMap[g.id] || [];
              return (
                <div key={g.id} style={{ marginBottom: '28px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#27ae60', marginBottom: '8px' }}>{g.name}</h3>
                  <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>#</th><th>Team</th>
                          <th style={{ textAlign: 'center' }}>P</th>
                          <th style={{ textAlign: 'center' }}>W</th>
                          <th style={{ textAlign: 'center' }}>L</th>
                          <th style={{ textAlign: 'center' }}>Pts</th>
                          <th style={{ textAlign: 'center' }}>For</th>
                          <th style={{ textAlign: 'center' }}>Against</th>
                          <th style={{ textAlign: 'center' }}>Diff</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((t, i) => (
                          <tr key={t.id}>
                            <td>
                              <span style={{ fontWeight: '700', color: i === 0 ? '#f39c12' : i === 1 ? '#7f8c8d' : '#bdc3c7' }}>
                                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                              </span>
                            </td>
                            <td><div style={{ fontWeight: '600' }}>{t.name}</div></td>
                            <td style={{ textAlign: 'center', color: '#7f8c8d' }}>{t.played}</td>
                            <td style={{ textAlign: 'center', color: '#27ae60', fontWeight: '700' }}>{t.wins}</td>
                            <td style={{ textAlign: 'center', color: '#e74c3c', fontWeight: '700' }}>{t.losses}</td>
                            <td style={{ textAlign: 'center', fontWeight: '800', fontSize: '15px', color: '#2c3e50' }}>{t.tournamentPoints}</td>
                            <td style={{ textAlign: 'center' }}>{t.ptsFor}</td>
                            <td style={{ textAlign: 'center' }}>{t.ptsAgainst}</td>
                            <td style={{ textAlign: 'center', fontWeight: '700', color: t.ptsFor - t.ptsAgainst >= 0 ? '#27ae60' : '#e74c3c' }}>
                              {t.ptsFor - t.ptsAgainst >= 0 ? '+' : ''}{t.ptsFor - t.ptsAgainst}
                            </td>
                          </tr>
                        ))}
                        {rows.length === 0 && (
                          <tr><td colSpan={9} style={{ textAlign: 'center', color: '#bdc3c7', padding: '24px' }}>No matches played yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          }
        </div>
      )}

      {/* ── Tab: Knockout Bracket ── */}
      {activeTab === 'bracket' && isGroupKnockout && (
        <div className="animate-fadein">
          <div className="page-header">
            <h2 style={{ fontSize: '18px' }}>Knockout Bracket</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setShowKnockoutSetup(true)} disabled={groups.length === 0}>
              {knockoutMatches.length > 0 ? '🔄 Regenerate Knockout' : '⚡ Generate Knockout'}
            </button>
          </div>
          {knockoutMatches.length === 0 ? (
            <div className="card"><div className="empty-state" style={{ padding: '48px' }}>
              <div className="icon">🏆</div>
              <h3>Knockout not generated yet</h3>
              <p>Complete group matches first, then generate the knockout bracket.</p>
              <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => setShowKnockoutSetup(true)} disabled={groups.length === 0}>
                Generate Knockout
              </button>
            </div></div>
          ) : (
            <>
              {/* Knockout match cards for scoring */}
              <div style={{ marginBottom: '24px' }}>
                {(() => {
                  const koRounds = [...new Set(knockoutMatches.map(m => m.knockout_round_num))].sort((a, b) => b - a);
                  return koRounds.map(rn => {
                    const rMatches = knockoutMatches.filter(m => m.knockout_round_num === rn && !m.is_bye)
                      .sort((a, b) => (a.knockout_position || 0) - (b.knockout_position || 0));
                    if (rMatches.length === 0) return null;
                    const label = rn === 2 ? 'Final' : rn === 4 ? 'Semi Finals' : rn === 8 ? 'Quarter Finals' : `Round of ${rn}`;
                    return (
                      <div key={rn} style={{ marginBottom: '20px' }}>
                        <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>{label}</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                          {rMatches.map(m => (
                            <MatchCard key={m.id} match={m} statusMap={statusMap} tournamentId={id} onRefresh={load} tournamentApproved={tournamentApproved} />
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Visual bracket */}
              <div className="card" style={{ padding: '20px', overflowX: 'auto' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#7f8c8d', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Bracket View
                </div>
                <KnockoutBracket
                  matches={knockoutMatches.filter(m => !m.is_bye)}
                  teamMap={teamMapForBracket}
                  navigate={navigate}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Tab: Matches (round_robin / single_elimination) ── */}
      {activeTab === 'bracket' && !isGroupKnockout && (
        <div className="animate-fadein">
          <div className="page-header">
            <h2 style={{ fontSize: '18px' }}>Matches ({matches.length})</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              {teams.length >= 2 && <button className="btn btn-secondary btn-sm" onClick={() => setShowMatchModal(true)}>⚡ Generate Matches</button>}
            </div>
          </div>
          {matches.length === 0 ? (
            <div className="card"><div className="empty-state" style={{ padding: '40px' }}><div className="icon">🗓</div><h3>No matches scheduled</h3><p>Add at least 2 teams, then generate matches</p></div></div>
          ) : rounds.map(round => (
            <div key={round} style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Round {round}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                {matches.filter(m => m.round === round).map(m => (
                  <MatchCard key={m.id} match={m} statusMap={statusMap} tournamentId={id} onRefresh={load} tournamentApproved={tournamentApproved} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Tab: Standings (round_robin only) ── */}
      {activeTab === 'standings' && !isGroupKnockout && (
        <div className="animate-fadein">
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="table">
              <thead>
                <tr><th>Rank</th><th>Team</th><th style={{ textAlign: 'center' }}>W</th><th style={{ textAlign: 'center' }}>L</th><th style={{ textAlign: 'center' }}>Pts For</th><th style={{ textAlign: 'center' }}>Pts Against</th><th style={{ textAlign: 'center' }}>Diff</th></tr>
              </thead>
              <tbody>
                {[...teams].sort((a, b) => b.wins - a.wins || (b.points_for - b.points_against) - (a.points_for - a.points_against)).map((t, i) => (
                  <tr key={t.id}>
                    <td><span style={{ fontWeight: '700', color: i === 0 ? '#f39c12' : i === 1 ? '#7f8c8d' : i === 2 ? '#cd7f32' : '#7f8c8d' }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}</span></td>
                    <td><div style={{ fontWeight: '600' }}>{t.name}</div></td>
                    <td style={{ textAlign: 'center', color: '#27ae60', fontWeight: '700' }}>{t.wins}</td>
                    <td style={{ textAlign: 'center', color: '#e74c3c', fontWeight: '700' }}>{t.losses}</td>
                    <td style={{ textAlign: 'center', fontWeight: '600' }}>{t.points_for}</td>
                    <td style={{ textAlign: 'center', fontWeight: '600' }}>{t.points_against}</td>
                    <td style={{ textAlign: 'center', fontWeight: '700', color: t.points_for - t.points_against >= 0 ? '#27ae60' : '#e74c3c' }}>{t.points_for - t.points_against >= 0 ? '+' : ''}{t.points_for - t.points_against}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {teams.length === 0 && <div className="empty-state"><div className="icon">📊</div><h3>No data yet</h3></div>}
          </div>
        </div>
      )}

      {/* ════════════ MODALS ════════════ */}

      {/* Add Team Modal */}
      {showTeamModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowTeamModal(false)}>
          <div className="modal animate-fadein">
            <h2>Add Team</h2>
            <form onSubmit={handleAddTeam}>
              <div className="form-group">
                <label>Player 1 *</label>
                <PlayerSelect
                  players={availablePlayers}
                  value={teamForm.player1_id}
                  onChange={id => setTeamForm(f => ({ ...f, player1_id: id }))}
                  placeholder="Search and select player…"
                  onCreateNew={handleCreatePlayer}
                  required
                />
              </div>
              {data.type === 'doubles' && (
                <div className="form-group">
                  <label>Player 2 *</label>
                  <PlayerSelect
                    players={availablePlayers}
                    value={teamForm.player2_id}
                    onChange={id => setTeamForm(f => ({ ...f, player2_id: id }))}
                    placeholder="Search and select player…"
                    excludeId={teamForm.player1_id}
                    onCreateNew={handleCreatePlayer}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Team Name (optional)</label>
                <input className="form-control" placeholder="Auto-generated if empty" value={teamForm.name} onChange={e => setTeamForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowTeamModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add Team'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Matches Modal (non-group) */}
      {showMatchModal && !isGroupKnockout && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowMatchModal(false)}>
          <div className={`modal animate-fadein${showManualMatch ? ' modal-lg' : ''}`}>
            <h2>Generate Matches</h2>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {['random', 'sequential', 'manual'].map(mode => (
                <button key={mode} onClick={() => { setMatchGenMode(mode); setShowManualMatch(mode === 'manual'); }}
                  style={{ flex: 1, padding: '10px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', background: matchGenMode === mode ? '#27ae60' : '#f0f4f8', color: matchGenMode === mode ? '#fff' : '#7f8c8d', border: 'none', cursor: 'pointer', textTransform: 'capitalize' }}>
                  {mode === 'random' ? '🎲 Random' : mode === 'sequential' ? '📋 Sequential' : '✏️ Manual'}
                </button>
              ))}
            </div>
            {showManualMatch && (
              <div style={{ marginBottom: '16px', maxHeight: '300px', overflowY: 'auto' }}>
                {manualMatches.map((mm, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                    <select className="form-control" value={mm.team1_id} onChange={e => setManualMatches(ms => ms.map((m, i) => i === idx ? { ...m, team1_id: e.target.value } : m))}>
                      <option value="">Team 1</option>{teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <select className="form-control" value={mm.team2_id} onChange={e => setManualMatches(ms => ms.map((m, i) => i === idx ? { ...m, team2_id: e.target.value } : m))}>
                      <option value="">Team 2</option>{teams.filter(t => t.id !== mm.team1_id).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <input className="form-control" type="number" min="1" max="10" value={mm.round} placeholder="Round" style={{ width: '70px' }}
                      onChange={e => setManualMatches(ms => ms.map((m, i) => i === idx ? { ...m, round: +e.target.value } : m))} />
                    <button className="btn btn-ghost btn-sm" style={{ color: '#e74c3c' }} onClick={() => setManualMatches(ms => ms.filter((_, i) => i !== idx))}>✕</button>
                  </div>
                ))}
                <button className="btn btn-outline btn-sm" onClick={() => setManualMatches(ms => [...ms, { team1_id: '', team2_id: '', round: 1 }])}>+ Add Match</button>
              </div>
            )}
            <p style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '16px' }}>⚠️ This will replace any unstarted matches.</p>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setShowMatchModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleGenerateMatches} disabled={saving}>{saving ? 'Generating...' : 'Generate'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Group Setup Modal */}
      {showGroupSetup && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowGroupSetup(false)}>
          <div className="modal animate-fadein" style={{ maxWidth: '520px' }}>
            <h2>Setup Groups</h2>
            <div style={{ marginBottom: '20px' }}>
              <div className="form-group">
                <label>Teams per Group</label>
                <input type="number" className="form-control" min={2} max={teams.length} value={teamsPerGroup}
                  onChange={e => setTeamsPerGroup(Math.max(2, Math.min(teams.length, +e.target.value)))} />
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                  → {Math.ceil(teams.length / teamsPerGroup)} groups · {teams.length} teams total
                </div>
              </div>

              <div className="form-group">
                <label>Assignment Mode</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['random', 'manual'].map(m => (
                    <button key={m} onClick={() => setAssignMode(m)}
                      style={{ flex: 1, padding: '10px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', background: assignMode === m ? '#27ae60' : '#f0f4f8', color: assignMode === m ? '#fff' : '#7f8c8d', border: 'none', cursor: 'pointer' }}>
                      {m === 'random' ? '🎲 Random' : '✏️ Manual'}
                    </button>
                  ))}
                </div>
              </div>

              {assignMode === 'manual' && (
                <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e8ecef', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '8px' }}>Assign each team to a group:</div>
                  {teams.map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={{ flex: 1, fontSize: '13px', fontWeight: '600' }}>{t.name}</div>
                      <select
                        className="form-control"
                        style={{ width: '140px' }}
                        value={manualGroupMap[t.id] ?? 0}
                        onChange={e => setManualGroupMap(m => ({ ...m, [t.id]: +e.target.value }))}
                      >
                        {Array.from({ length: Math.ceil(teams.length / teamsPerGroup) }, (_, i) => (
                          <option key={i} value={i}>Group {String.fromCharCode(65 + i)}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '16px' }}>⚠️ Existing group assignments will be replaced.</p>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setShowGroupSetup(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSetupGroups} disabled={saving}>{saving ? 'Setting up…' : 'Create Groups'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Knockout Setup Modal */}
      {showKnockoutSetup && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowKnockoutSetup(false)}>
          <div className="modal animate-fadein" style={{ maxWidth: '460px' }}>
            <h2>Generate Knockout</h2>
            <div className="form-group">
              <label>Teams qualifying per group</label>
              <input type="number" className="form-control" min={1} max={Math.max(1, ...groups.map(g => (standingsMap[g.id] || []).length))} value={qualifyPerGroup}
                onChange={e => setQualifyPerGroup(Math.max(1, +e.target.value))} />
            </div>

            {/* Preview qualified teams */}
            {groups.length > 0 && (() => {
              const seeds = buildKnockoutSeeds(groups, qualifyPerGroup, standingsMap);
              const bracketSize = nextPow2(seeds.length);
              const byeCount    = bracketSize - seeds.length;
              return (
                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '10px' }}>
                    {seeds.length} qualified teams → Bracket of {bracketSize}{byeCount > 0 ? ` (${byeCount} bye${byeCount > 1 ? 's' : ''})` : ''}
                  </div>
                  {groups.map(g => {
                    const standings = standingsMap[g.id] || [];
                    const qualifiers = standings.slice(0, qualifyPerGroup);
                    return (
                      <div key={g.id} style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#27ae60', marginBottom: '4px' }}>{g.name}</div>
                        {qualifiers.map((t, i) => (
                          <div key={t.id} style={{ fontSize: '12px', color: '#555', paddingLeft: '8px' }}>
                            {i + 1}. {t.name} <span style={{ color: '#7f8c8d' }}>({t.tournamentPoints} pts)</span>
                          </div>
                        ))}
                        {qualifiers.length < qualifyPerGroup && (
                          <div style={{ fontSize: '12px', color: '#e74c3c', paddingLeft: '8px' }}>
                            ⚠️ Only {qualifiers.length} team{qualifiers.length !== 1 ? 's' : ''} in this group
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            <p style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '16px' }}>⚠️ Existing knockout matches will be replaced.</p>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setShowKnockoutSetup(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleGenerateKnockout} disabled={saving}>{saving ? 'Generating…' : '⚡ Generate Bracket'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Result Modal */}
      {addResultMatch && (
        <AddResultModal
          match={addResultMatch}
          onClose={() => setAddResultMatch(null)}
          onSuccess={() => { setAddResultMatch(null); load(); }}
        />
      )}

      <AppDialog dialog={dialog} onClose={() => setDialog(null)} />
    </div>
  );
}

// ─── Score display helper ─────────────────────────────────────────────────────
// For completed matches renders set-by-set scores like "11-9, 2-11, 11-8 · 2-1"
// For live/scheduled matches renders total score numbers.

function MatchScore({ match: m }) {
  const isDone = m.status === 'completed';
  const t1Win  = m.winner_team_id === m.team1_id;
  const t2Win  = m.winner_team_id === m.team2_id;

  if (isDone) {
    let sets = [];
    try { sets = JSON.parse(m.set_scores || '[]'); } catch { /* ignore */ }

    if (sets.length > 0) {
      const t1Sets = m.team1_sets ?? sets.filter(s => Number(s.t1) > Number(s.t2)).length;
      const t2Sets = m.team2_sets ?? sets.filter(s => Number(s.t2) > Number(s.t1)).length;
      return (
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '3px' }}>
            {sets.map((s, i) => {
              const s1 = Number(s.t1), s2 = Number(s.t2);
              const setWinner = s1 > s2 ? 1 : s2 > s1 ? 2 : 0;
              return (
                <span key={i} style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>
                  <span style={{ color: setWinner === 1 ? '#27ae60' : '#e74c3c', fontWeight: '700' }}>{s.t1}</span>
                  <span style={{ color: '#bdc3c7', margin: '0 1px' }}>-</span>
                  <span style={{ color: setWinner === 2 ? '#27ae60' : '#e74c3c', fontWeight: '700' }}>{s.t2}</span>
                  {i < sets.length - 1 && <span style={{ color: '#ccc', marginLeft: '3px' }}>,</span>}
                </span>
              );
            })}
          </div>
          <div style={{ fontSize: '11px', color: '#7f8c8d', fontWeight: '600' }}>
            <span style={{ color: t1Win ? '#27ae60' : '#2c3e50' }}>{t1Sets}</span>
            <span style={{ color: '#bdc3c7', margin: '0 3px' }}>–</span>
            <span style={{ color: t2Win ? '#27ae60' : '#2c3e50' }}>{t2Sets}</span>
            {' sets'}
          </div>
        </div>
      );
    }
  }

  // Fallback: total score
  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
      <span style={{ fontSize: '20px', fontWeight: '800', color: t1Win ? '#27ae60' : '#2c3e50', minWidth: '24px', textAlign: 'center' }}>{m.team1_score ?? '—'}</span>
      <span style={{ color: '#bdc3c7' }}>:</span>
      <span style={{ fontSize: '20px', fontWeight: '800', color: t2Win ? '#27ae60' : '#2c3e50', minWidth: '24px', textAlign: 'center' }}>{m.team2_score ?? '—'}</span>
    </div>
  );
}

// ─── GroupMatchCard ───────────────────────────────────────────────────────────

function GroupMatchCard({ match: m, statusMap, scheduleEdit, savingSchedule, onScheduleChange, onScheduleSave, onNavigate, onAddResult, tournamentApproved }) {
  const [copied, setCopied] = useState(false);
  const isLive = m.status === 'active';
  const isDone = m.status === 'completed';

  const handleStart = async () => {
    if (!tournamentApproved && m.status !== 'active') return;
    if (m.status === 'scheduled') {
      await updateDoc(doc(db, 'matches', m.id), {
        court_positions: '{}', court_number: m.court_number || 1,
        current_server_team: m.team1_id, server_number: 2, is_first_serve: true, status: 'setup',
      });
    }
    onNavigate(`/matches/${m.id}/live`);
  };

  const editing = !!scheduleEdit;

  return (
    <div className="card" style={{ padding: '14px', border: isLive ? '2px solid #27ae60' : '1.5px solid #eee' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: '11px', color: '#7f8c8d' }}>
          {m.scheduled_time
            ? `📅 ${new Date(m.scheduled_time).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}`
            : 'No time set'} · Court {m.court_number || '—'}
        </span>
        <span className={`badge badge-${m.status}`}>{statusMap[m.status]} {m.status}</span>
      </div>

      {/* Teams & score */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <div style={{ flex: 1, fontWeight: m.winner_team_id === m.team1_id ? '800' : '600', fontSize: '13px' }}>
          {m.winner_team_id === m.team1_id && '🏆 '}{m.team1_name}
        </div>
        <MatchScore match={m} />
        <div style={{ flex: 1, textAlign: 'right', fontWeight: m.winner_team_id === m.team2_id ? '800' : '600', fontSize: '13px' }}>
          {m.winner_team_id === m.team2_id && '🏆 '}{m.team2_name}
        </div>
      </div>

      {/* Schedule edit */}
      {!isDone && (
        <div style={{ background: '#f8f9fa', borderRadius: '6px', padding: '8px', marginBottom: '8px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '6px', alignItems: 'center' }}>
            <input
              type="datetime-local" className="form-control" style={{ fontSize: '12px', padding: '4px 8px' }}
              value={scheduleEdit?.time ?? (m.scheduled_time ? m.scheduled_time.slice(0, 16) : '')}
              onChange={e => onScheduleChange('time', e.target.value)}
            />
            <input
              type="number" className="form-control" style={{ width: '60px', fontSize: '12px', padding: '4px 8px' }}
              placeholder="Court" min={1} max={20}
              value={scheduleEdit?.court ?? (m.court_number || '')}
              onChange={e => onScheduleChange('court', e.target.value)}
            />
            <button className="btn btn-primary btn-sm" onClick={onScheduleSave} disabled={!editing || savingSchedule} style={{ fontSize: '11px', padding: '4px 10px' }}>
              {savingSchedule ? '…' : '✓'}
            </button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!isDone && (
        <div style={{ display: 'flex', gap: '6px' }}>
          <button className={`btn ${isLive ? 'btn-primary' : 'btn-outline'} btn-sm`} style={{ flex: 1, justifyContent: 'center' }} onClick={handleStart}>
            {isLive ? '🔴 View Live' : m.status === 'setup' ? '⚙️ Setup' : '▶ Start'}
          </button>
          <button className="btn btn-sm" style={{ flex: 1, justifyContent: 'center', background: 'transparent', border: '1px solid #e67e22', color: '#e67e22', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }} onClick={onAddResult}>
            {isLive ? '⏹ Stop & Add Result' : '+ Add Result'}
          </button>
        </div>
      )}
      {isDone && (
        <div style={{ textAlign: 'center', fontSize: '12px', color: '#7f8c8d' }}>
          ✅ {m.winner_team_id === m.team1_id ? m.team1_name : m.team2_name} won
        </div>
      )}
      <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${process.env.REACT_APP_BASENAME || ''}/matches/${m.id}/live`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
        style={{ padding: '4px 10px', borderRadius: '5px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', border: `1px solid ${copied ? '#27ae60' : 'rgba(0,0,0,0.15)'}`, background: copied ? '#eafaf1' : '#f8f9fa', color: copied ? '#27ae60' : '#7f8c8d', marginTop: '6px', width: '100%', transition: 'all 0.2s' }}>
        {copied ? '✓ Copied!' : '🔗 Copy Live Link'}
      </button>
    </div>
  );
}

// ─── MatchCard (for non-group matches) ───────────────────────────────────────

function MatchCard({ match: m, statusMap, tournamentId, onRefresh, tournamentApproved }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [approvalAlert, setApprovalAlert] = useState(false);
  const [showAddResult, setShowAddResult] = useState(false);

  const handleCopyLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}${process.env.REACT_APP_BASENAME || ''}/matches/${m.id}/live`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStart = async () => {
    if (!tournamentApproved && m.status !== 'active') { setApprovalAlert(true); return; }
    if (m.status === 'scheduled') {
      await updateDoc(doc(db, 'matches', m.id), {
        court_positions: '{}', court_number: m.court_number || 1,
        current_server_team: m.team1_id, server_number: 2, is_first_serve: true, status: 'setup',
      });
    }
    navigate(`/matches/${m.id}/live`);
  };

  const isLive = m.status === 'active';
  const isDone = m.status === 'completed';

  return (
    <div className="card" style={{ padding: '16px', border: isLive ? '2px solid #27ae60' : '1.5px solid #eee', animation: isLive ? 'glow 2s infinite' : 'none' }}>
      {approvalAlert && <AppDialog title="Approval Required" message="Tournament must be approved before matches can start." onClose={() => setApprovalAlert(false)} />}
      {showAddResult && <AddResultModal match={m} onClose={() => setShowAddResult(false)} onSuccess={() => { setShowAddResult(false); onRefresh(); }} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ fontSize: '12px', color: '#7f8c8d' }}>Court {m.court_number}</span>
        <span className={`badge badge-${m.status}`}>{statusMap[m.status]} {m.status}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: m.winner_team_id === m.team1_id ? '800' : '600', fontSize: '14px' }}>{m.winner_team_id === m.team1_id && '🏆 '}{m.team1_name || 'TBD'}</div>
          <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '2px' }}>{m.team1_player1_name}{m.team1_player2_name ? ` & ${m.team1_player2_name}` : ''}</div>
        </div>
        <MatchScore match={m} />
        <div style={{ flex: 1, textAlign: 'right' }}>
          <div style={{ fontWeight: m.winner_team_id === m.team2_id ? '800' : '600', fontSize: '14px' }}>{m.winner_team_id === m.team2_id && '🏆 '}{m.team2_name || 'TBD'}</div>
          <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '2px' }}>{m.team2_player1_name}{m.team2_player2_name ? ` & ${m.team2_player2_name}` : ''}</div>
        </div>
      </div>
      {!isDone && (
        <button className={`btn ${isLive ? 'btn-primary' : 'btn-outline'} btn-sm`} style={{ width: '100%', marginTop: '12px', justifyContent: 'center', opacity: (!tournamentApproved && !isLive) ? 0.6 : 1 }} onClick={handleStart}>
          {isLive ? '🔴 View Live' : m.status === 'setup' ? '⚙️ Setup & Score' : '▶ Start Match'}
        </button>
      )}
      {!isDone && (
        <button className="btn btn-sm" style={{ width: '100%', marginTop: '6px', justifyContent: 'center', background: 'transparent', border: '1px solid #e67e22', color: '#e67e22', borderRadius: '6px', padding: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }} onClick={() => setShowAddResult(true)}>
          {isLive ? '⏹ Stop & Add Result' : '+ Add Result'}
        </button>
      )}
      {isDone && <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: '#7f8c8d' }}>Match complete · Winner: {m.winner_team_id === m.team1_id ? m.team1_name : m.team2_name}</div>}
      <button onClick={handleCopyLink} style={{ padding: '6px 10px', borderRadius: '5px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', border: `1px solid ${copied ? '#27ae60' : 'rgba(0,0,0,0.15)'}`, background: copied ? '#eafaf1' : '#f8f9fa', color: copied ? '#27ae60' : '#7f8c8d', marginTop: '8px', width: '100%', transition: 'all 0.2s' }}>
        {copied ? '✓ Copied!' : '🔗 Copy Live Link'}
      </button>
    </div>
  );
}

// ─── AddResultModal ───────────────────────────────────────────────────────────

function AddResultModal({ match: m, onClose, onSuccess }) {
  const setsToWin    = m.sets_to_win  || 1;
  const pointsToWin  = m.points_to_win || 11;
  const maxSets      = setsToWin * 2 - 1;
  const isFinalMatch = m.match_type === 'knockout' && !m.next_match_id && m.knockout_round_num === 2;

  const [setScores,   setSetScores]  = useState([{ t1: '', t2: '' }]);
  const [submitting,  setSubmitting] = useState(false);
  const [error,       setError]      = useState('');
  const [celebrated,  setCelebrated] = useState(false); // shows champion screen after final

  function getSetWinner(t1, t2) {
    const n1 = parseInt(t1, 10), n2 = parseInt(t2, 10);
    if (isNaN(n1) || isNaN(n2)) return null;
    if (n1 >= pointsToWin && n1 - n2 >= 2) return 1;
    if (n2 >= pointsToWin && n2 - n1 >= 2) return 2;
    return null;
  }

  function computeState(scores) {
    let t1Sets = 0, t2Sets = 0, matchWinner = null;
    const results = scores.map(s => {
      if (matchWinner) return null;
      const w = getSetWinner(s.t1, s.t2);
      if (w === 1) t1Sets++;
      if (w === 2) t2Sets++;
      if (t1Sets >= setsToWin) matchWinner = 1;
      if (t2Sets >= setsToWin) matchWinner = 2;
      return w;
    });
    return { t1Sets, t2Sets, matchWinner, results };
  }

  const { t1Sets, t2Sets, matchWinner, results } = computeState(setScores);

  // True when the last set has a determined winner but the match isn't over yet
  const canAddNextSet = !matchWinner && results[setScores.length - 1] !== null && results[setScores.length - 1] !== undefined && setScores.length < maxSets;

  const handleChange = (idx, team, val) => {
    setSetScores(s => s.map((row, i) => i === idx ? { ...row, [team]: val } : row));
    setError('');
  };

  const handleAddNextSet = () => {
    setSetScores(s => [...s, { t1: '', t2: '' }]);
  };

  const handleUndo = () => {
    setSetScores(prev => {
      if (prev.length === 1) return [{ t1: '', t2: '' }];
      const last = prev[prev.length - 1];
      if (last.t1 === '' && last.t2 === '') {
        const next = prev.slice(0, -1);
        next[next.length - 1] = { t1: '', t2: '' };
        return next;
      }
      const next = [...prev];
      next[next.length - 1] = { t1: '', t2: '' };
      return next;
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (!matchWinner) { setError('Enter valid scores to determine a winner first.'); return; }
    setSubmitting(true);
    try {
      const validSets  = setScores.filter((_, i) => results[i]);
      let t1Total = 0, t2Total = 0;
      validSets.forEach(s => { t1Total += parseInt(s.t1, 10) || 0; t2Total += parseInt(s.t2, 10) || 0; });
      const t1SetsWon    = validSets.filter((_, i) => results[i] === 1).length;
      const t2SetsWon    = validSets.filter((_, i) => results[i] === 2).length;
      const winnerTeamId = matchWinner === 1 ? m.team1_id : m.team2_id;
      const loserTeamId  = matchWinner === 1 ? m.team2_id : m.team1_id;
      const winnerPts    = matchWinner === 1 ? t1Total : t2Total;
      const loserPts     = matchWinner === 1 ? t2Total : t1Total;

      // Fetch both teams to get current stats + player IDs
      const [t1Snap, t2Snap] = await Promise.all([
        getDoc(doc(db, 'teams', m.team1_id)),
        getDoc(doc(db, 'teams', m.team2_id)),
      ]);
      const t1 = t1Snap.data() || {};
      const t2 = t2Snap.data() || {};

      // Fetch all players on both teams
      const allPlayerIds = [t1.player1_id, t1.player2_id, t2.player1_id, t2.player2_id].filter(Boolean);
      const playerSnaps  = await Promise.all(allPlayerIds.map(pid => getDoc(doc(db, 'players', pid))));
      const playerMap    = {};
      playerSnaps.forEach((snap, i) => { if (snap.exists()) playerMap[allPlayerIds[i]] = snap.data(); });

      const batch = writeBatch(db);

      // Update match document
      batch.update(doc(db, 'matches', m.id), {
        status:        'completed',
        winner_team_id: winnerTeamId,
        team1_score:   t1Total,
        team2_score:   t2Total,
        team1_sets:    t1SetsWon,
        team2_sets:    t2SetsWon,
        set_scores:    JSON.stringify(validSets.map(s => ({ t1: parseInt(s.t1, 10) || 0, t2: parseInt(s.t2, 10) || 0 }))),
        completed_at:  new Date().toISOString(),
      });

      // Update winner team
      const wTeam = winnerTeamId === m.team1_id ? t1 : t2;
      batch.update(doc(db, 'teams', winnerTeamId), {
        wins:           (wTeam.wins || 0) + 1,
        points_for:     (wTeam.points_for || 0) + winnerPts,
        points_against: (wTeam.points_against || 0) + loserPts,
      });

      // Update loser team
      const lTeam = loserTeamId === m.team1_id ? t1 : t2;
      batch.update(doc(db, 'teams', loserTeamId), {
        losses:         (lTeam.losses || 0) + 1,
        points_for:     (lTeam.points_for || 0) + loserPts,
        points_against: (lTeam.points_against || 0) + winnerPts,
      });

      // Update player stats
      const t1PlayerIds = [t1.player1_id, t1.player2_id].filter(Boolean);
      const t2PlayerIds = [t2.player1_id, t2.player2_id].filter(Boolean);
      t1PlayerIds.forEach(pid => {
        const pd = playerMap[pid]; if (!pd) return;
        const won = matchWinner === 1;
        batch.update(doc(db, 'players', pid), {
          wins:           (pd.wins || 0) + (won ? 1 : 0),
          losses:         (pd.losses || 0) + (won ? 0 : 1),
          points_scored:  (pd.points_scored || 0) + t1Total,
          points_against: (pd.points_against || 0) + t2Total,
          matches_played: (pd.matches_played || 0) + 1,
        });
      });
      t2PlayerIds.forEach(pid => {
        const pd = playerMap[pid]; if (!pd) return;
        const won = matchWinner === 2;
        batch.update(doc(db, 'players', pid), {
          wins:           (pd.wins || 0) + (won ? 1 : 0),
          losses:         (pd.losses || 0) + (won ? 0 : 1),
          points_scored:  (pd.points_scored || 0) + t2Total,
          points_against: (pd.points_against || 0) + t1Total,
          matches_played: (pd.matches_played || 0) + 1,
        });
      });

      // Advance winner to next knockout match
      if (m.next_match_id && winnerTeamId) {
        const field = m.next_match_slot === 'team1' ? 'team1_id' : 'team2_id';
        batch.update(doc(db, 'matches', m.next_match_id), { [field]: winnerTeamId });
      }

      // Auto-complete tournament when the Final is finished
      if (isFinalMatch && m.tournament_id) {
        batch.update(doc(db, 'tournaments', m.tournament_id), {
          status:           'completed',
          champion_team_id: winnerTeamId,
          runner_up_team_id: loserTeamId,
          completed_at:     new Date().toISOString(),
        });
      }

      await batch.commit();

      if (isFinalMatch) {
        // Show celebration screen before closing
        setCelebrated(true);
      } else {
        onSuccess();
      }
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const winnerName  = matchWinner === 1 ? m.team1_name : m.team2_name;
  const loserName   = matchWinner === 1 ? m.team2_name : m.team1_name;

  // ── Champion celebration screen (shown after final result is saved) ──────────
  if (celebrated) {
    return (
      <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.85)' }}>
        <div className="animate-fadein" style={{ textAlign: 'center', padding: '32px 24px', maxWidth: '420px', margin: '0 auto' }}>
          {/* Confetti dots */}
          <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
            {Array.from({ length: 40 }).map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                left: `${Math.random() * 100}%`,
                top: `-${Math.random() * 20 + 5}%`,
                width: `${Math.random() * 10 + 6}px`,
                height: `${Math.random() * 10 + 6}px`,
                borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                background: ['#f39c12','#27ae60','#3498db','#e74c3c','#9b59b6','#1abc9c'][Math.floor(Math.random() * 6)],
                animation: `confettiFall ${Math.random() * 3 + 2}s linear ${Math.random() * 2}s infinite`,
                opacity: 0.85,
              }} />
            ))}
          </div>
          <style>{`
            @keyframes confettiFall {
              0%   { transform: translateY(0) rotate(0deg);   opacity: 1; }
              100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
            }
            @keyframes trophyBounce {
              0%, 100% { transform: scale(1); }
              50%       { transform: scale(1.15); }
            }
          `}</style>

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: '80px', lineHeight: 1, marginBottom: '16px', animation: 'trophyBounce 1s ease-in-out infinite' }}>🏆</div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#f39c12', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>Tournament Champion</div>
            <div style={{ fontSize: '28px', fontWeight: '900', color: '#fff', marginBottom: '24px', lineHeight: 1.2 }}>{winnerName}</div>

            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '14px 20px', marginBottom: '28px' }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Runner-up</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'rgba(255,255,255,0.8)' }}>🥈 {loserName}</div>
            </div>

            <button
              className="btn btn-primary"
              style={{ width: '100%', fontSize: '15px', padding: '12px', background: '#f39c12', border: 'none' }}
              onClick={onSuccess}
            >
              View Tournament Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal animate-fadein" style={{ maxWidth: '420px' }}>
        <h2 style={{ marginBottom: '4px' }}>Add Match Result</h2>
        <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '20px' }}>
          Best of {maxSets} set{maxSets > 1 ? 's' : ''} · First to {setsToWin} · {pointsToWin} pts per set
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 56px 1fr', gap: '8px', marginBottom: '6px' }}>
          <div style={{ textAlign: 'center', fontWeight: '700', fontSize: '13px', color: '#2c3e50' }}>{m.team1_name}</div>
          <div />
          <div style={{ textAlign: 'center', fontWeight: '700', fontSize: '13px', color: '#2c3e50' }}>{m.team2_name}</div>
        </div>
        {setScores.map((s, idx) => {
          const w      = results[idx];
          const locked = w !== null && w !== undefined && idx < setScores.length - 1;
          return (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 56px 1fr', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <input type="number" min="0" className="form-control"
                style={{ textAlign: 'center', fontWeight: '700', fontSize: '16px', background: w === 1 ? '#eafaf1' : w === 2 ? '#fff5f5' : '#fff', border: w === 1 ? '1.5px solid #27ae60' : w === 2 ? '1.5px solid #e74c3c' : undefined, color: w === 1 ? '#27ae60' : '#2c3e50' }}
                value={s.t1} onChange={e => handleChange(idx, 't1', e.target.value)} disabled={locked} placeholder="0" />
              <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: '600', color: w === 1 ? '#27ae60' : w === 2 ? '#e74c3c' : '#7f8c8d' }}>
                {w === 1 ? '✓ Win' : w === 2 ? '✗ Loss' : `Set ${idx + 1}`}
              </div>
              <input type="number" min="0" className="form-control"
                style={{ textAlign: 'center', fontWeight: '700', fontSize: '16px', background: w === 2 ? '#eafaf1' : w === 1 ? '#fff5f5' : '#fff', border: w === 2 ? '1.5px solid #27ae60' : w === 1 ? '1.5px solid #e74c3c' : undefined, color: w === 2 ? '#27ae60' : '#2c3e50' }}
                value={s.t2} onChange={e => handleChange(idx, 't2', e.target.value)} disabled={locked} placeholder="0" />
            </div>
          );
        })}

        {/* Manual next-set button — user triggers when ready */}
        {canAddNextSet && (
          <button
            type="button"
            onClick={handleAddNextSet}
            style={{ width: '100%', marginBottom: '12px', padding: '8px', borderRadius: '6px', border: '1.5px dashed #27ae60', background: '#f0faf4', color: '#27ae60', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
          >
            + Add Set {setScores.length + 1}
          </button>
        )}
        {maxSets > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', margin: '16px 0 12px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '26px', fontWeight: '800', color: matchWinner === 1 ? '#27ae60' : '#2c3e50', lineHeight: 1 }}>{t1Sets}</div>
              <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '2px' }}>sets</div>
            </div>
            <div style={{ color: '#bdc3c7', fontWeight: '600', fontSize: '18px' }}>:</div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '26px', fontWeight: '800', color: matchWinner === 2 ? '#27ae60' : '#2c3e50', lineHeight: 1 }}>{t2Sets}</div>
              <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '2px' }}>sets</div>
            </div>
          </div>
        )}
        {matchWinner && (
          <div style={{ textAlign: 'center', padding: '10px 16px', background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: '8px', marginBottom: '12px', color: '#1e8449', fontWeight: '700', fontSize: '14px' }}>
            🏆 {winnerName} wins the match!
          </div>
        )}
        {error && <div style={{ color: '#e74c3c', fontSize: '13px', marginBottom: '10px', padding: '8px', background: '#fdf2f2', borderRadius: '6px' }}>{error}</div>}
        <div className="modal-footer" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleUndo}>↩ Undo</button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={!matchWinner || submitting}>
            {submitting ? 'Saving…' : '✓ Save Result'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PlayerSelect ─────────────────────────────────────────────────────────────
// Select2-style combobox: click to open, type to filter, click to select.
// Props:
//   players      – full list of available players (already sorted A→Z)
//   value        – selected player id ('' = none)
//   onChange     – (id) => void
//   placeholder  – trigger text when nothing selected
//   excludeId    – id to hide from list (prevents picking same player twice)
//   onCreateNew  – async (name) => newPlayerId   (shows "+ New Player" row)
//   required     – adds a hidden required input for native form validation

function PlayerSelect({ players, value, onChange, placeholder = 'Select player…', excludeId, onCreateNew, required }) {
  const [open,     setOpen]     = useState(false);
  const [search,   setSearch]   = useState('');
  const [creating, setCreating] = useState(false);
  const [newName,  setNewName]  = useState('');
  const [createErr, setCreateErr] = useState('');
  const [creating2, setCreating2] = useState(false); // loading state for create

  const wrapRef   = useRef(null);
  const inputRef  = useRef(null);
  const createRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setCreating(false); setSearch(''); setCreateErr(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Auto-focus search when dropdown opens
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  // Auto-focus create input when creating
  useEffect(() => { if (creating && createRef.current) createRef.current.focus(); }, [creating]);

  const selected = players.find(p => p.id === value);

  // Alphabetical, filtered, excluding already-chosen partner
  const filtered = players
    .filter(p => p.id !== excludeId && p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleSelect = (player) => {
    onChange(player.id);
    setOpen(false);
    setSearch('');
    setCreating(false);
    setCreateErr('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) { setCreateErr('Name is required.'); return; }
    setCreating2(true);
    setCreateErr('');
    try {
      const newId = await onCreateNew(trimmed);
      onChange(newId);
      setOpen(false);
      setCreating(false);
      setSearch('');
      setNewName('');
    } catch (err) {
      setCreateErr(err.message || 'Error creating player');
    } finally {
      setCreating2(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Hidden input for native required validation */}
      {required && (
        <input
          tabIndex={-1}
          required
          value={value}
          onChange={() => {}}
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
        />
      )}

      {/* Trigger button */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '7px 12px', borderRadius: '6px', border: `1px solid ${open ? '#27ae60' : '#dde1e7'}`,
          background: '#fff', cursor: 'pointer', fontSize: '14px', minHeight: '38px',
          boxShadow: open ? '0 0 0 3px rgba(39,174,96,0.12)' : 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          userSelect: 'none',
        }}
      >
        <span style={{ color: selected ? '#2c3e50' : '#aab0ba', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.name : placeholder}
        </span>
        {selected
          ? <span onClick={handleClear} style={{ color: '#bdc3c7', fontWeight: '700', fontSize: '16px', lineHeight: 1, padding: '0 2px', cursor: 'pointer' }} title="Clear">×</span>
          : <span style={{ color: '#bdc3c7', fontSize: '11px', marginLeft: '6px' }}>{open ? '▲' : '▼'}</span>
        }
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1050,
          background: '#fff', border: '1px solid #e0e4ea', borderRadius: '8px',
          boxShadow: '0 6px 24px rgba(0,0,0,0.13)', overflow: 'hidden',
        }}>
          {/* Search box */}
          <div style={{ padding: '8px', borderBottom: '1px solid #f0f4f8' }}>
            <input
              ref={inputRef}
              className="form-control"
              placeholder="Type to search…"
              value={search}
              onChange={e => { setSearch(e.target.value); setCreating(false); setNewName(e.target.value); setCreateErr(''); }}
              onClick={e => e.stopPropagation()}
              style={{ fontSize: '13px' }}
            />
          </div>

          {/* Options list */}
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filtered.length === 0 && !creating && (
              <div style={{ padding: '12px 14px', fontSize: '13px', color: '#aab0ba', textAlign: 'center' }}>
                No players found
              </div>
            )}
            {filtered.map(p => (
              <div
                key={p.id}
                onClick={() => handleSelect(p)}
                style={{
                  padding: '9px 14px', cursor: 'pointer', fontSize: '14px',
                  background: p.id === value ? '#f0faf4' : undefined,
                  color: p.id === value ? '#27ae60' : '#2c3e50',
                  fontWeight: p.id === value ? '600' : '400',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}
                onMouseEnter={e => { if (p.id !== value) e.currentTarget.style.background = '#f8f9fa'; }}
                onMouseLeave={e => { if (p.id !== value) e.currentTarget.style.background = ''; }}
              >
                {p.id === value && <span style={{ color: '#27ae60', fontSize: '12px' }}>✓</span>}
                {p.name}
              </div>
            ))}
          </div>

          {/* Create new player row */}
          <div style={{ borderTop: '1px solid #f0f4f8' }}>
            {!creating ? (
              <div
                onClick={() => { setCreating(true); setNewName(search); setCreateErr(''); }}
                style={{ padding: '9px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#27ae60', display: 'flex', alignItems: 'center', gap: '6px' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f0faf4'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                + New Player{search.trim() ? ` "${search.trim()}"` : ''}
              </div>
            ) : (
              <div style={{ padding: '8px' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', gap: '6px', marginBottom: createErr ? '4px' : 0 }}>
                  <input
                    ref={createRef}
                    className="form-control"
                    placeholder="Player name…"
                    value={newName}
                    onChange={e => { setNewName(e.target.value); setCreateErr(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreate(); } if (e.key === 'Escape') { setCreating(false); setCreateErr(''); } }}
                    style={{ flex: 1, fontSize: '13px' }}
                  />
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleCreate} disabled={creating2}>
                    {creating2 ? '…' : 'Add'}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setCreating(false); setCreateErr(''); }}>✕</button>
                </div>
                {createErr && <div style={{ fontSize: '12px', color: '#e74c3c', marginTop: '4px' }}>{createErr}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
